import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import BarrierActionForm from 'src/features/parkingActions/components/BarrierActionForm';
import { type Action } from 'src/features/parkingActions/routes/AllActions';
import { actionMessages } from 'src/features/parkingActions/utils/messages';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { useListReasons } from 'src/features/points/api/useListReasons';
import {
    usePointAction,
    type PointActionFormSchemaValues,
    pointActionFormSchema,
} from 'src/features/points/api/usePointAction';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHead,
    DialogHeader,
    DialogPortal,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { PointType } from 'src/features/points/types';

interface BarrierActionModalProps extends Omit<React.ComponentProps<typeof Button>, 'title'> {
    action: Action;
    triggerComponent: React.ReactNode;
}

const BarrierActionModalForm = ({ action, triggerComponent, ...buttonProps }: BarrierActionModalProps) => {
    const [selectedPointType, setSelectedPointType] = React.useState<PointType>();
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { parkingId } = useParking();
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const form = useForm<PointActionFormSchemaValues>({
        resolver: zodResolver(pointActionFormSchema),
        defaultValues: {
            pointId: '',
            reason: '',
            action_type: action.actionType,
        },
    });

    const { data: points } = useListPoints({ parkingId, limit: 100, config: { enabled: !!parkingId } });
    const { data: reasons } = useListReasons({
        parkingId,
        limit: 100,
        offset: 0,
        config: { enabled: !!parkingId },
    });

    const { mutate, isPending } = usePointAction({
        onSuccess: (_, data) => {
            onOpenChange(false);
            toast({
                title: formatMessage(actionMessages.barrierActionSuccess),
                description: formatMessage(actionMessages.barrierActionSuccessDetails, {
                    action: data?.action,
                    point: data?.pointId,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = ({ pointId, reason }: PointActionFormSchemaValues) => {
        if (!isPending) {
            mutate({
                parkingId,
                pointId,
                action: action.action,
                action_type: action.actionType,
                reason: reason?.split('..')[0],
            });
        }
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogTrigger asChild>{triggerComponent}</DialogTrigger>
            <DialogPortal>
                <DialogContent>
                    <DialogHeader className="flex-row gap-4 space-y-0">
                        <SquareIcon
                            icon={action.icon}
                            className="shrink-0"
                            variant={buttonProps.variant === 'destructiveLight' ? 'destructiveLight' : undefined}
                        />
                        <DialogHead className="space-y-0">
                            <DialogTitle>{formatMessage(action.title)}</DialogTitle>
                            {action.subtitle && <DialogDescription>{formatMessage(action.subtitle)}</DialogDescription>}
                        </DialogHead>
                    </DialogHeader>

                    <DialogSeparator />

                    <DialogBody>
                        <BarrierActionForm
                            form={form}
                            onSubmit={onSubmit}
                            points={points?.results}
                            reasons={reasons?.results}
                            actionType={action.actionType}
                            selectedPointState={[selectedPointType, setSelectedPointType]}
                        />
                    </DialogBody>

                    <DialogSeparator />

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                {formatMessage(generalMessages.cancel)}
                            </Button>
                        </DialogClose>

                        <Button
                            disabled={isPending || !form.formState.isValid}
                            type="submit"
                            onClick={form.handleSubmit(onSubmit)}
                            className="flex-1"
                        >
                            {isPending ? (
                                <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                            ) : null}
                            {formatMessage(generalMessages.execute)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
};

export default BarrierActionModalForm;
